import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { addReactiontoPost, commentOnPost, fetchPostComments } from '../../redux/FeedSlice/feedSlice';
import { isUsablePhoto } from '../NetworkingPage/NetContent/NetPipelineRow';

const formatPostDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

// Relative "2h ago / 1d ago" formatting for the post header.
const relativeTime = (dateString) => {
    if (!dateString) return '';
    const then = new Date(dateString).getTime();
    if (Number.isNaN(then)) return '';
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return formatPostDate(dateString);
};

const AVATAR_GRADIENTS = [
    ['#3F57E0', '#6377F0'], ['#7C3AED', '#A78BFA'], ['#059669', '#34D399'],
    ['#DC2626', '#F87171'], ['#B45309', '#FCD34D'], ['#0369A1', '#38BDF8'],
];
const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(' ').filter(Boolean);
    return (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase();
};
const gradientFor = (name) => {
    if (!name) return AVATAR_GRADIENTS[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
};

function PostAvatar({ name, photo, size = 46 }) {
    const usable = isUsablePhoto(photo);
    const [g1] = gradientFor(name);
    if (usable) {
        return <img src={photo} alt={name || ''} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} onError={(e) => { e.target.style.display = 'none'; }} />;
    }
    return (
        <div className="rounded-full grid place-items-center text-white font-bold flex-shrink-0 select-none"
            style={{ width: size, height: size, fontSize: size * 0.36, background: `linear-gradient(135deg, ${g1}, #6377F0)`, boxShadow: 'inset 0 -1px 2px rgba(0,0,0,.15)' }}>
            {initials(name)}
        </div>
    );
}

function ReactionStack() {
    const r = ['#3F57E0', '#E11D48', '#F59E0B'];
    return (
        <div className="flex items-center">
            {r.map((c, i) => (
                <div key={i} className="grid place-items-center rounded-full border-2 border-white" style={{ marginLeft: i ? -6 : 0, width: 20, height: 20, background: c }}>
                    {i === 0 && <svg width="9" height="9" viewBox="0 0 24 24" fill="#fff"><path d="M12 21s-7-4.35-9.5-8.5C.5 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5.5 3.5 3.5 7C19 16.65 12 21 12 21z" /></svg>}
                    {i === 1 && <span style={{ fontSize: 9 }}>👏</span>}
                    {i === 2 && <span style={{ fontSize: 9 }}>💡</span>}
                </div>
            ))}
        </div>
    );
}

const PostAttachment = ({ attachment }) => {
    if (!attachment) return null;
    switch (attachment.type) {
        case 'video':
            return <video src={attachment.url} controls className="w-full rounded-xl border bg-black" />;
        case 'image':
            return <img src={attachment.url} alt="Post attachment" className="w-full rounded-xl border" />;
        default:
            return null;
    }
};

const PostsCard = ({ post, selectedConnection, accountId }) => {
    const dispatch = useDispatch();

    const { commentPostLoading } = useSelector((state) => state.feeds);
    const currentUser = useSelector((state) => state.auth.user);
    const { text, attachments, reactionCounter, commentCounter, parsedDatetime, shareUrl } = post;

    // Liked state is derived from the post's persisted userReactions (survives
    // refresh), then toggled optimistically so the UI updates without a refetch.
    const isLikedFromData = Array.isArray(post.userReactions) && post.userReactions.length > 0;
    const [liked, setLiked] = useState(isLikedFromData);
    const [likeCount, setLikeCount] = useState(reactionCounter || 0);
    const [likeBusy, setLikeBusy] = useState(false);

    useEffect(() => {
        setLiked(Array.isArray(post.userReactions) && post.userReactions.length > 0);
        setLikeCount(post.reactionCounter || 0);
    }, [post.socialId, post.userReactions, post.reactionCounter]);
    const {
        data: postComments = [],
        loading: commentsLoading,
        error: commentsError,
    } = useSelector((state) => state.feeds.commentsByPostId[post.socialId] || {});

    const [expanded, setExpanded] = useState(false);
    const [newComment, setNewComment] = useState("");

    const handleToggleComments = () => {
        if (!expanded && commentsLoading !== 'pending') {
            dispatch(fetchPostComments({ accountId, postId: post.socialId }));
        }
        setExpanded((prev) => !prev);
    };

    // Like is one-way from the webapp: Unipile has no "remove reaction" endpoint,
    // so once liked we lock the button rather than offer a fake unlike. To unlike,
    // the user must do it on LinkedIn directly.
    const handleLike = async () => {
        if (likeBusy || liked || !accountId) return;
        // optimistic update — no refresh needed
        setLiked(true);
        setLikeCount((c) => c + 1);
        setLikeBusy(true);
        try {
            await dispatch(addReactiontoPost({ socialId: post.socialId, accountId, reactionType: "like" })).unwrap();
        } catch (e) {
            // revert on failure
            setLiked(false);
            setLikeCount((c) => Math.max(0, c - 1));
        } finally {
            setLikeBusy(false);
        }
    };

    const handleSendComment = () => {
        if (!newComment.trim()) return;
        dispatch(commentOnPost({ socialId: post.socialId, accountId, text: newComment.trim() }));
        setNewComment("");
    };

    const authorName = selectedConnection?.name || 'LinkedIn Member';
    const authorRole = [selectedConnection?.title, selectedConnection?.organization_name].filter(Boolean).join(' · ');

    return (
        <div className="bg-white rounded-xl border border-[#E6E8EE] overflow-hidden mb-4" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.06)' }}>
            {/* header */}
            <div className="flex items-center gap-3 px-5 pt-4 pb-3">
                <PostAvatar name={authorName} photo={selectedConnection?.profile_picture} size={46} />
                <div className="flex-1 min-w-0 text-left">
                    <p className="text-[14.5px] font-bold text-[#0F172A] leading-tight truncate">{authorName}</p>
                    {authorRole && <p className="text-[11.5px] text-[#64748B] mt-0.5 truncate">{authorRole}</p>}
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">{relativeTime(parsedDatetime)} · 🌐 Public</p>
                </div>
                <button className="w-8 h-8 grid place-items-center rounded-lg text-[#94A3B8] hover:bg-gray-50 flex-shrink-0">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                </button>
            </div>

            {/* body */}
            {text && <div className="px-5 pb-3.5 text-[13.5px] leading-relaxed text-[#334155] text-left whitespace-pre-wrap">{text}</div>}

            {/* attachment */}
            {attachments?.length > 0 && (
                <div className="mx-5 mb-3.5 rounded-xl border border-[#EFF1F5] overflow-hidden">
                    <PostAttachment attachment={attachments[0]} />
                </div>
            )}

            {/* reaction summary */}
            <div className="px-5 pb-3 flex items-center gap-2">
                <ReactionStack />
                <span className="text-[11.5px] text-[#64748B]">{likeCount} reactions</span>
                <span className="flex-1" />
                <span className="text-[11.5px] text-[#64748B]">{commentCounter || 0} comments</span>
            </div>

            {/* action bar */}
            <div className="grid grid-cols-3 border-t border-[#EFF1F5]">
                <button
                    onClick={handleLike}
                    disabled={liked || likeBusy}
                    title={liked ? 'You liked this. To remove it, unlike the post on LinkedIn.' : 'Like'}
                    className={`py-2.5 inline-flex items-center justify-center gap-2 text-[13px] font-semibold border-r border-[#EFF1F5] transition-colors ${liked ? 'text-[#3F57E0] bg-[#EEF1FE] cursor-default' : 'text-[#64748B] hover:bg-gray-50 disabled:opacity-60'}`}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill={liked ? '#3F57E0' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M12 21s-7-4.35-9.5-8.5C.5 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5.5 3.5 3.5 7C19 16.65 12 21 12 21z" /></svg>
                    {liked ? 'Liked' : 'Like'}
                </button>
                <button onClick={handleToggleComments} className="py-2.5 inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-[#64748B] hover:bg-gray-50 border-r border-[#EFF1F5] transition-colors">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    Comment
                </button>
                <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="py-2.5 inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-[#64748B] hover:bg-gray-50 hover:text-[#0A66C2] transition-colors">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#0A66C2"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                    View on LinkedIn
                </a>
            </div>

            {/* comment composer + thread */}
            {expanded && (
                <div className="px-5 py-3.5 border-t border-[#EFF1F5] bg-[#FAFBFD]">
                    <div className="flex gap-2.5 items-center">
                        <PostAvatar name={currentUser?.name || 'You'} photo={currentUser?.profilePicture || currentUser?.photo} size={32} />
                        <div className="flex-1 flex gap-2 bg-white border border-[#E6E8EE] rounded-full items-center pl-3.5 pr-1 py-1">
                            <input
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Write a comment…"
                                className="flex-1 text-[13px] outline-none bg-transparent"
                            />
                            <button
                                onClick={handleSendComment}
                                disabled={commentPostLoading}
                                className="px-3.5 py-1.5 rounded-full bg-[#3F57E0] hover:bg-[#3346B8] text-white text-[12px] font-semibold transition-colors disabled:opacity-60"
                            >
                                {commentPostLoading ? '…' : 'Send'}
                            </button>
                        </div>
                    </div>
                    {commentsLoading === 'pending' && <div className="text-[12px] text-[#94A3B8] mt-3">Loading comments…</div>}
                    {commentsError && <div className="text-[12px] text-red-500 mt-3">Error loading comments.</div>}
                    {postComments.slice(0, 5).map((c) => {
                        // Unipile returns the logged-in user's own comments with author
                        // "You" — show the real user's name + avatar instead.
                        const isOwn = c.is_author || c.author === 'You';
                        const displayName = isOwn ? (currentUser?.name || 'You') : c.author;
                        const displayPhoto = isOwn ? (currentUser?.profilePicture || currentUser?.photo) : c.authorImage;
                        return (
                            <div key={c.id} className="flex gap-2.5 mt-3">
                                <PostAvatar name={displayName} photo={displayPhoto} size={32} />
                                <div className="flex-1 bg-white border border-[#EFF1F5] px-3.5 py-2.5" style={{ borderRadius: '4px 12px 12px 12px' }}>
                                    <div className="text-[12.5px] font-bold text-[#0F172A] text-left">{displayName}</div>
                                    <div className="text-[13px] text-[#334155] mt-0.5 text-left">{c.text}</div>
                                    <div className="text-[11px] text-[#94A3B8] mt-1.5 text-left">{formatPostDate(c.date)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PostsCard;
